/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Link } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { cn } from '@/lib/cn';
import { chip } from '@/lib/chip';
import { initials, ownerColour } from '@/lib/avatar';
import { fmtDate } from '@/lib/date';
import { MessageIcon, PaperclipIcon } from '@/lib/icons';
import { isOverdue, priorityMeta, type Task } from '@/types/task';
import { Calendar } from 'lucide-react';

/**
 * One card.
 *
 * `.card` and `.card-title` are part of the selector contract the browser suite
 * holds — see docs/react-migration/plan.md §8. Renaming either is a decision plus
 * a test edit.
 *
 * The card is a **link**, not a div with an onClick. The task modal is a route
 * now, so middle-click and "open in new tab" work, and a task can be pasted into
 * a chat and actually open.
 */
export function TaskCard({
  task,
  draggable = false,
  onClickCapture,
}: {
  task: Task;
  draggable?: boolean;
  onClickCapture?: (e: React.MouseEvent) => void;
}) {
  // Counts, not arrays: the board payload does not carry the rows.
  const done = task.checklistDone;
  const total = task.checklistTotal;
  const overdue = isOverdue(task);
  const prio = priorityMeta(task.priority);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: !draggable,
  });

  return (
    <Link
      ref={setNodeRef}
      {...(draggable ? { ...attributes, ...listeners } : {})}
      onClickCapture={onClickCapture}
      to={`/board/task/${task.id}`}
      // **The column's colour arrives through this attribute, not a class.**
      // It used to be a `VERTICAL_SKIN` map of `bg-*`/`border-*` utilities, which
      // could only ever be a flat fill — utilities are single declarations, and
      // the card wants a gradient, a matching border and a matching hover border
      // that all move together. `index.css` sets `--v` and `--v-bg` from this
      // attribute once and every rule reads them, so the three cannot drift.
      // `data-vertical` is already the board's word for this: `.col-body` carries
      // it, and `board-view` aims its drops with it.
      data-vertical={task.vertical}
      className={cn(
        'card group relative block rounded-card border p-3 no-underline',
        'transition-[transform,box-shadow,border-color] duration-fast ease-out-soft',
        'hover:-translate-y-px hover:shadow-raised',
        // The original stays in place, faded, while the overlay follows the
        // pointer: a card that vanishes from its column mid-drag makes the
        // column jump and the drop target move under you.
        isDragging && 'opacity-40',
      )}
    >
      {task.unseen && (
        <span
          title="Updated since you last looked"
          className="unseen-dot absolute right-2.5 top-2.5 size-2 rounded-full bg-action"
        />
      )}

      <div className="flex items-start justify-between gap-2">
        {/* A title is free text and somebody will paste a 180-character URL into
            it. Two things are needed and `break-words` alone is not enough:
            `overflow-wrap: anywhere` is the one that also shrinks the element's
            **min-content width**, and `min-w-0` is what lets a flex item go below
            that width at all. With only `break-words` the card still measured as
            wide as its longest unbroken run, pushed the column past the viewport
            and gave the whole page a horizontal scrollbar — 9px of it, which
            `robustness` measures and I first tried to fix with the wrong one of
            the two. */}
        <div className="card-title min-w-0 [overflow-wrap:anywhere] text-base font-semibold leading-snug text-ink">
          {task.title}
        </div>
        {task.unread > 0 && (
          <span className="shrink-0 rounded-full bg-action px-1.5 text-2xs font-bold leading-5 text-bg">
            {task.unread}
          </span>
        )}
      </div>

      {task.description && (
        <p className="mt-1 mb-0 line-clamp-2 text-xs leading-relaxed text-ink-muted">{task.description}</p>
      )}

      {task.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {task.tags.map((t) => (
            <span
              key={t.id}
              className="tag-chip rounded-full px-2 py-0.5 text-2xs font-semibold"
              style={chip(t.color)}
            >
              {t.name}
            </span>
          ))}
        </div>
      )}

      {task.created_by_name && (
        <div className="mt-2 text-2xs text-ink-faint">Created by {task.created_by_name}</div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span
          title={`${prio.label} — ${prio.name}`}
          className={`prio-badge prio-p${prio.id}`}
        >
          {prio.label}
        </span>

        {/* On a borrowed card the pin chip *replaces* this: both would name the
            same circle, and a card has room for one.

            **On every card otherwise — including a board that is one circle.**
            It was briefly hidden there, on the argument that the word is already
            in the heading and printing it once per card is repetition. Abhishek
            wants it on every card, and there is a good reason it was ever
            unconditional: `robustness` asserts a single-circle viewer's cards
            are labelled, because they get no switcher, and a card copied into a
            message or read across a desk carries no heading with it. */}
        {task.circle && !task.pinnedHere && (
          <span className="circle-badge" style={chip(task.circle.color)} title={task.circle.name}>
            {task.circle.name}
          </span>
        )}

        <PinBadge task={task} />

        {task.finish_date && (
          // The calendar glyph is vanilla's, and it earns its place: the pill
          // beside it holds `3/8`, and two bare numbers in two identical pills
          // are only told apart by reading them.
          <span className={cn('pill date-pill', overdue && 'overdue')}>
            <Calendar aria-hidden />
            {fmtDate(task.finish_date)}
          </span>
        )}

        {total > 0 && (
          <span className="pill checklist-pill">
            {done}/{total}
          </span>
        )}
      </div>

      {total > 0 && (
        <div className="progress-track mt-2">
          <div className="progress-fill" style={{ width: `${Math.round((done / total) * 100)}%` }} />
        </div>
      )}

      {/* The bottom row: what is attached, what has been said, and who is on it.
          All three are the things you scan a board for without opening anything.
          **Always rendered**, like vanilla's `.card-bottom` — the message count
          shows `0` rather than vanishing, so "nobody has said anything" and "this
          card does not show comments" are not the same sight. */}
      <div className="card-bottom mt-2.5 flex items-center justify-between gap-3">
        <div className="card-icons">
          {task.attachmentCount > 0 && (
            <span>
              <PaperclipIcon />
              {task.attachmentCount}
            </span>
          )}
          <span>
            <MessageIcon />
            {task.messageCount}
          </span>
        </div>

        {/* **Assignees only, and that is the point of the split.** A card exists
            to answer "who is doing this" at a glance; putting watchers in the
            same row of faces is what made that question unanswerable. The
            participants are on the task, one click away.

            Overlapping initials, newest behind. Three at most and then a count:
            a card with eight avatars on it stops being scannable.

            **A task with nobody assigned shows no faces**, even when people are
            watching it — unassigned has to look unassigned, or the board cannot
            show you what has been picked up. */}
        {task.assignees.length > 0 && (
          <span className="avatar-group">
            {task.assignees.slice(0, 3).map((o, i) => (
              <span
                key={o}
                title={o}
                className="avatar"
                style={{ background: ownerColour(o), marginLeft: i === 0 ? 0 : -7 }}
              >
                {initials(o)}
              </span>
            ))}
            {task.assignees.length > 3 && (
              <span
                className="avatar avatar-more"
                title={task.assignees.slice(3).join(', ')}
                style={{ marginLeft: -7 }}
              >
                +{task.assignees.length - 3}
              </span>
            )}
          </span>
        )}
      </div>
    </Link>
  );
}

/**
 * The pin chip — **two different facts, worded differently.**
 *
 * - **from Core** — you are seeing this card because it was pinned *into* your
 *   circle. This chip replaces the circle badge, since both would name the same
 *   circle and a card has room for one.
 * - **Pinned to Extended** — your circle's task is also on somebody else's
 *   board. Named while there is exactly one destination; beyond that it falls
 *   back to "Pinned elsewhere", which is short enough never to wrap.
 *
 * The three flags this reads were being parsed and then ignored, so a borrowed
 * card said "Pinned to Extended" — the home circle's sentence, shown to the
 * borrower, naming a circle they may not even be in.
 */
function PinBadge({ task }: { task: Task }) {
  const home = task.circle?.name ?? 'another circle';

  if (task.pinnedHere) {
    return (
      <span
        className="pin-badge is-here rounded-full border border-dashed border-border px-2 py-0.5 text-2xs text-ink-muted"
        title={`Pinned into your circle from ${home}`}
      >
        from {home}
      </span>
    );
  }

  if (!task.pinnedElsewhere) return null;

  const named = task.pins.map((p) => p.name);
  // A borrower is sent only the pins into circles it is in, so `pins` can be
  // shorter than `pinCount`. Naming the one destination is only honest when both
  // agree that there is exactly one.
  const label = task.pinCount === 1 && named.length === 1 ? `Pinned to ${named[0]}` : 'Pinned elsewhere';
  return (
    <span
      className="pin-badge rounded-full border border-dashed border-border px-2 py-0.5 text-2xs text-ink-muted"
      title={named.length ? `Pinned to ${named.join(', ')}` : 'Also pinned into another circle'}
    >
      {label}
    </span>
  );
}
